import { z } from 'zod'
import { isLoanSignatureDateValid, type LoanSignatureCase } from '@/app/utils/loan/loan-signature-date'

// Constants for bank names
export const BANK_OTHER = 'Autre'
export const BANK_UNKNOWN = 'Je ne sais pas'

// Date schema for signature date
const signatureDateSchema = z
    .string({ required_error: 'Veuillez indiquer la date de signature du prêt.' })
    .min(1, 'Veuillez indiquer la date de signature du prêt.')
    .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Merci de vérifier la date de signature du prêt.')

// Bank name schema
const bankNameSchema = z
    .string({ required_error: 'Veuillez sélectionner une banque.' })
    .min(1, 'Veuillez sélectionner une banque.')

// Custom bank name schema (when "Autre" is selected)
const customBankNameSchema = z.string().optional()

// Main financing form schema
export const financingFormSchema = z
    .object({
        bank_name: bankNameSchema,
        custom_bank_name: customBankNameSchema,
        signature_date: signatureDateSchema,
    })
    // Conditional validation: custom_bank_name required if bank_name is "Autre"
    .refine(
        (data) => {
            if (data.bank_name === BANK_OTHER) {
                return !!data.custom_bank_name && data.custom_bank_name.trim().length > 0
            }
            return true
        },
        {
            message: 'Veuillez renseigner le nom de la banque.',
            path: ['custom_bank_name'],
        }
    )

// Schema with signature-date bounds per project type (parité parcours legacy Symfony)
export const createFinancingFormSchemaWithContext = (signatureCase: LoanSignatureCase) => {
    return financingFormSchema.refine(
        (data) => isLoanSignatureDateValid(data.signature_date, signatureCase),
        {
            message:
                signatureCase === 'switching'
                    ? 'La date de signature doit être comprise entre il y a 40 ans et hier.'
                    : signatureCase === 'repurchase'
                    ? 'La date de signature doit être comprise entre demain et dans 12 mois.'
                    : "La date de signature doit être comprise entre aujourd'hui et dans 12 mois.",
            path: ['signature_date'],
        }
    )
}

// Infer TypeScript type from schema
export type FinancingFormData = z.infer<typeof financingFormSchema>

// Default values factory
export const createFinancingFormDefaults = (): FinancingFormData => ({
    bank_name: '',
    custom_bank_name: undefined,
    signature_date: '',
})

// Safe parse with detailed error handling
export const parseFinancingForm = (data: unknown) => {
    return financingFormSchema.safeParse(data)
}

// Export validators for individual fields
export const financingValidators = {
    bankName: bankNameSchema,
    signatureDate: signatureDateSchema,
} as const

// Helper functions
export const isCustomBankRequired = (bankName: string): boolean => {
    return bankName === BANK_OTHER
}

export const shouldShowBankUnknownOption = (isNewLoan: boolean): boolean => {
    // Only allow "Je ne sais pas" for new loans (Lagarde)
    return isNewLoan
}
